import { useState, useEffect } from 'react';
import { db, storage } from '../firebase';
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc,
  query, orderBy, onSnapshot, serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// ─── Listings ───

export function useListings(filterOptions = {}) {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'listings'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setListings(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (error) => {
      console.error('Error fetching listings:', error);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const filtered = listings.filter(l => {
    if (filterOptions.status && l.status !== filterOptions.status) return false;
    if (filterOptions.brand && l.brand !== filterOptions.brand) return false;
    if (filterOptions.category && l.category !== filterOptions.category) return false;
    return true;
  });

  return { listings: filtered, allListings: listings, loading };
}

export async function addListing(data) {
  const docRef = await addDoc(collection(db, 'listings'), {
    ...data,
    price: Number(data.price),
    year: Number(data.year),
    status: data.status || 'Listed',
    photos: data.photos || [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return docRef.id;
}

export async function updateListing(id, data) {
  await updateDoc(doc(db, 'listings', id), {
    ...data,
    price: Number(data.price),
    year: Number(data.year),
    updatedAt: serverTimestamp()
  });
}

export async function deleteListing(id) {
  await deleteDoc(doc(db, 'listings', id));
}

// ─── Leads ───

export function useLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'leads'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setLeads(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (error) => {
      console.error('Error fetching leads:', error);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  return { leads, loading };
}

export async function addLead(data) {
  const docRef = await addDoc(collection(db, 'leads'), {
    ...data,
    askingPrice: Number(data.askingPrice) || 0,
    status: 'New Lead',
    notes: '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return docRef.id;
}

export async function updateLead(id, data) {
  await updateDoc(doc(db, 'leads', id), {
    ...data,
    updatedAt: serverTimestamp()
  });
}

// ─── Photo Upload ───

export async function uploadPhotos(folder, id, files) {
  const urls = [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const path = `${folder}/${id}/${Date.now()}_${i}_${file.name}`;
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, file);
    const url = await getDownloadURL(snapshot.ref);
    urls.push(url);
  }
  return urls;
}

// ─── Seed sample data ───

export async function seedSampleData() {
  const listingsRef = collection(db, 'listings');
  const snapshot = await getDocs(listingsRef);
  if (!snapshot.empty) return false;

  const samples = [
    { name: "Surron Light Bee X", brand: "Surron", year: 2024, price: 14500, condition: "Like New", battery: "Excellent", mileage: "320 km", location: "Dubai Marina", photos: [], category: "surron", status: "Listed", description: "Barely used Surron Light Bee X. Full service history, upgraded tires." },
    { name: "Talaria Sting R MX4", brand: "Talaria", year: 2024, price: 16800, condition: "Brand New", battery: "Excellent", mileage: "0 km", location: "JLT", photos: [], category: "talaria", status: "Listed", description: "Brand new in box. UAE spec with warranty." },
    { name: "Surron Storm Bee", brand: "Surron", year: 2023, price: 22000, condition: "Good", battery: "Good", mileage: "1,200 km", location: "Al Quoz", photos: [], category: "surron", status: "Listed", description: "Road legal Storm Bee. Minor cosmetic wear, mechanically perfect." },
    { name: "Kugoo G2 Pro", brand: "Kugoo", year: 2024, price: 3200, condition: "Like New", battery: "Excellent", mileage: "80 km", location: "Downtown Dubai", photos: [], category: "scooter", status: "Listed", description: "Powerful commuter scooter. Dual suspension, great range." },
    { name: "Talaria Sting MX3", brand: "Talaria", year: 2023, price: 11500, condition: "Good", battery: "Good", mileage: "890 km", location: "Jumeirah", photos: [], category: "talaria", status: "Listed", description: "Well-maintained MX3. Perfect for desert riding." },
    { name: "Surron Ultra Bee", brand: "Surron", year: 2024, price: 28500, condition: "Brand New", battery: "Excellent", mileage: "0 km", location: "Business Bay", photos: [], category: "surron", status: "Listed", description: "Top-of-the-line Ultra Bee. Road legal, 90km/h top speed." },
  ];

  for (const listing of samples) {
    await addDoc(listingsRef, { ...listing, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  }
  return true;
}
